import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Select from "react-select";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { ArrowLeft, Calendar, DollarSign } from "feather-icons-react/build/IconComponents";
import DefaultEditor from "react-simple-wysiwyg";
import { toast } from "react-toastify";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import {
  createPosOrderPayment,
  getPosOrderPayments,
  updatePosOrder,
  downloadPosOrderPdf,
} from "../../../api/pos-sales-order";

const PAYMENT_TYPE_OPTIONS = [
  { value: "cash", labelKey: "Cash" },
  { value: "card", labelKey: "Card" },
];

const ORDER_STATUS_OPTIONS = [
  { value: "Pending", labelKey: "Pending" },
  { value: "Completed", labelKey: "Completed" },
  { value: "Cancelled", labelKey: "Cancelled" },
];

const PosSalesModel = ({
  selectedOrder,
  salesDetails,
  onRefresh,
  onDeleteClick,
}) => {
  const t = (key) => (window?.translate ? window.translate(key) : key);
  const paymentTypeOptions = PAYMENT_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));
  const orderStatusOptions = ORDER_STATUS_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));

  const [paymentsData, setPaymentsData] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [editForm, setEditForm] = useState({
    status: "Pending",
    paymentReceiver: "",
    paymentNote: "",
    saleNote: "",
    staffNote: "",
    paymentType: "cash",
    receivedAmount: 0,
    payingAmount: 0,
    change: 0,
    items: [],
  });

  const [paymentForm, setPaymentForm] = useState({
    date: new Date(),
    reference: "",
    receivedAmount: "",
    payingAmount: "",
    type: "cash",
    paymentNote: "",
    saleNote: "",
    staffNote: "",
    change: 0,
  });

  useEffect(() => {
    if (!salesDetails) return;

    setEditForm({
      status: salesDetails?.status || "Pending",
      paymentReceiver: salesDetails?.paymentReceiver || "",
      paymentNote: salesDetails?.paymentNote || "",
      saleNote: salesDetails?.saleNote || "",
      staffNote: salesDetails?.staffNote || "",
      paymentType: salesDetails?.paymentType || "cash",
      receivedAmount: salesDetails?.receivedAmount || 0,
      payingAmount: salesDetails?.payingAmount || 0,
      change: salesDetails?.change || 0,
      items: Array.isArray(salesDetails?.items) ? salesDetails.items : [],
    });

    setPaymentForm((prev) => ({
      ...prev,
      reference: salesDetails?.orderId || "",
      saleNote: salesDetails?.saleNote || "",
      staffNote: salesDetails?.staffNote || "",
      paymentNote: salesDetails?.paymentNote || "",
    }));
  }, [salesDetails]);

  const grandTotal = useMemo(
    () => Number(salesDetails?.grandTotal || salesDetails?.totalAmount || 0),
    [salesDetails]
  );

  const paidAmount = useMemo(
    () => Number(salesDetails?.paidAmount || 0),
    [salesDetails]
  );

  const dueAmount = useMemo(
    () => Number(salesDetails?.dueAmount || 0),
    [salesDetails]
  );

  const fetchPayments = async () => {
    if (!selectedOrder?._id) return;
    try {
      setLoadingPayments(true);
      const res = await getPosOrderPayments(selectedOrder._id);
      const payload = res?.data?.data || res?.data || res || {};
      const paymentList = Array.isArray(payload?.payments) ? payload.payments : [];
      setPaymentsData(paymentList);
    } catch (error) {
      toast.error(error?.message || t("Failed to fetch payments"));
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (!selectedOrder?._id) {
      setPaymentsData([]);
      return;
    }
    fetchPayments();
  }, [selectedOrder?._id]);

  const handleUpdateSale = async () => {
    if (!selectedOrder?._id) return;

    try {
      const payload = {
        status: editForm.status,
        paymentReceiver: editForm.paymentReceiver,
        paymentNote: editForm.paymentNote,
        saleNote: editForm.saleNote,
        staffNote: editForm.staffNote,
        paymentType: editForm.paymentType,
        receivedAmount: Number(editForm.receivedAmount || 0),
        payingAmount: Number(editForm.payingAmount || 0),
        change: Number(editForm.change || 0),
        items: (editForm.items || []).map((item) => ({
          productId: item?.productId?._id || item?.productId || item?.product || null,
          name: item?.name || item?.productId?.name || t("Item"),
          quantity: Number(item?.quantity || 1),
          price: Number(item?.price || 0),
        })),
      };

      await updatePosOrder(selectedOrder._id, payload);
      toast.success(t("POS sale updated successfully"));
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || t("Failed to update sale"));
    }
  };

  const handleCreatePayment = async () => {
    if (!selectedOrder?._id) return;

    const received = Number(paymentForm.receivedAmount || 0);
    const paying = Number(paymentForm.payingAmount || 0);

    if (paying <= 0) {
      toast.error(t("Paying amount must be greater than 0"));
      return;
    }

    try {
      await createPosOrderPayment(selectedOrder._id, {
        type: paymentForm.type,
        receivedAmount: received,
        payingAmount: paying,
        change: Number(paymentForm.change || received - paying || 0),
        paymentReceiver: salesDetails?.paymentReceiver || t("Admin"),
        paymentNote: paymentForm.paymentNote,
        saleNote: paymentForm.saleNote,
        staffNote: paymentForm.staffNote,
      });

      toast.success(t("Payment created successfully"));
      setPaymentForm({
        date: new Date(),
        reference: salesDetails?.orderId || "",
        receivedAmount: "",
        payingAmount: "",
        type: "cash",
        paymentNote: "",
        saleNote: salesDetails?.saleNote || "",
        staffNote: salesDetails?.staffNote || "",
        change: 0,
      });

      fetchPayments();
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || t("Failed to create payment"));
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedOrder?._id) return;

    try {
      const blob = await downloadPosOrderPdf(selectedOrder._id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pos-order-${salesDetails?.orderId || "invoice"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.message || t("Failed to download pdf"));
    }
  };

  const handlePaymentInput = (field, value) => {
    setPaymentForm((prev) => {
      const updated = { ...prev, [field]: value };

      const received = Number(updated.receivedAmount || 0);
      const paying = Number(updated.payingAmount || 0);
      updated.change = received > paying ? received - paying : 0;

      return updated;
    });
  };

  return (
    <div>
      <div className="modal fade" id="sales-details-new">
        <div className="modal-dialog sales-details-modal">
          <div className="modal-content">
            <div className="page-header p-4 border-bottom mb-0">
              <div className="add-item d-flex align-items-center">
                <div className="page-title modal-datail">
                  <h4 className="mb-0 me-2">{t("Sales Detail")}</h4>
                </div>
              </div>
              <ul className="table-top-head">
                <li>
                  <Link to="#" onClick={handleDownloadPdf}>
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="pdf" />
                  </Link>
                </li>
              </ul>
              <div className="page-btn">
                <Link to="#" className="btn btn-secondary" data-bs-dismiss="modal">
                  <ArrowLeft className="me-2" /> {t("Back to Sales")}
                </Link>
              </div>
            </div>

            <div className="card border-0">
              <div className="card-body pb-0">
                <div className="invoice-box table-height" style={{ maxWidth: 1600, width: "100%", padding: 0, fontSize: 14 }}>
                  <div className="row sales-details-items d-flex">
                    <div className="col-md-4 details-item">
                      <h6>{t("User Info")}</h6>
                      <h4 className="mb-1">{salesDetails?.user?.name || t("N/A")}</h4>
                      <p className="mb-0">{t("Email")}: <span>{salesDetails?.user?.email || t("N/A")}</span></p>
                      <p className="mb-0">{t("Customer ID")}: <span>{salesDetails?.customerId || t("N/A")}</span></p>
                    </div>

                    <div className="col-md-4 details-item">
                      <h6>{t("Invoice Info")}</h6>
                      <p className="mb-0">{t("Reference")}:
                        <span className="fs-16 text-primary ms-2">{salesDetails?.orderId || "--"}</span>
                      </p>
                      <p className="mb-0">{t("Date")}:
                        <span className="ms-2 text-gray-9">
                          {salesDetails?.createdAt ? new Date(salesDetails.createdAt).toLocaleDateString() : "--"}
                        </span>
                      </p>
                      <p className="mb-0">{t("Status")}:
                        <span className="badge badge-success ms-2">{salesDetails?.status ? t(salesDetails?.status) : "--"}</span>
                      </p>
                      <p className="mb-0">{t("Payment Status")}:
                        <span className="badge badge-soft-success shadow-none badge-xs d-inline-flex align-items-center ms-2">
                          <i className="ti ti-point-filled" />
                          {salesDetails?.paymentStatus ? t(salesDetails?.paymentStatus) : "--"}
                        </span>
                      </p>
                    </div>
                  </div>

                  <h5 className="order-text">{t("Order Summary")}</h5>
                  <div className="table-responsive no-pagination mb-3">
                    <table className="table datanew">
                      <thead>
                        <tr>
                          <th>{t("Product")}</th>
                          <th>{t("Qty")}</th>
                          <th>{t("Price")}</th>
                          <th>{t("Total")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesDetails?.items?.length > 0 ? (
                          salesDetails.items.map((item, index) => {
                            const price = Number(item?.price || 0);
                            const qty = Number(item?.quantity || 0);
                            return (
                              <tr key={index}>
                                <td>{item?.name || item?.productId?.name || t("Item")}</td>
                                <td>{qty}</td>
                                <td>{price.toFixed(2)}</td>
                                <td>{(price * qty).toFixed(2)}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">{t("No items found")}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-6 ms-auto">
                    <div className="total-order w-100 max-widthauto m-auto mb-4">
                      <ul className="border-1 rounded-1">
                        <li className="border-bottom">
                          <h4 className="border-end">{t("Grand Total")}</h4>
                          <h5>$ {grandTotal.toFixed(2)}</h5>
                        </li>
                        <li className="border-bottom">
                          <h4 className="border-end">{t("Paid")}</h4>
                          <h5>$ {paidAmount.toFixed(2)}</h5>
                        </li>
                        <li className="border-bottom">
                          <h4 className="border-end">{t("Due")}</h4>
                          <h5>$ {dueAmount.toFixed(2)}</h5>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="edit-sales-new">
        <div className="modal-dialog edit-sales-modal">
          <div className="modal-content">
            <div className="page-wrapper p-0 m-0">
              <div className="content p-0">
                <div className="page-header p-4 mb-0">
                  <div className="add-item new-sale-items d-flex">
                    <div className="page-title">
                      <h4>{t("Edit Sales")}</h4>
                    </div>
                    <button type="button" className="close" data-bs-dismiss="modal" aria-label={t("Close")}>
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                </div>

                <div className="card border-0">
                  <div className="card-body pb-0">
                    <div className="table-responsive no-pagination mb-3">
                      <table className="table datanew">
                        <thead>
                          <tr>
                            <th>{t("Product")}</th>
                            <th>{t("Qty")}</th>
                            <th>{t("Price")}</th>
                            <th>{t("Total")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(editForm.items || []).map((item, index) => (
                            <tr key={index}>
                              <td>{item?.name || item?.productId?.name || t("Item")}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={item?.quantity || 1}
                                  onChange={(e) => {
                                    const items = [...editForm.items];
                                    items[index].quantity = Number(e.target.value || 1);
                                    setEditForm((prev) => ({ ...prev, items }));
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={item?.price || 0}
                                  onChange={(e) => {
                                    const items = [...editForm.items];
                                    items[index].price = Number(e.target.value || 0);
                                    setEditForm((prev) => ({ ...prev, items }));
                                  }}
                                />
                              </td>
                              <td>{((Number(item?.quantity || 0)) * (Number(item?.price || 0))).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="row">
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">{t("Status")}</label>
                          <Select
                            classNamePrefix="react-select"
                            options={orderStatusOptions}
                            value={orderStatusOptions.find((x) => x.value === editForm.status) || null}
                            onChange={(selected) =>
                              setEditForm((prev) => ({ ...prev, status: selected?.value || "Pending" }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">{t("Received Amount")}</label>
                          <input
                            type="number"
                            className="form-control"
                            value={editForm.receivedAmount}
                            onChange={(e) =>
                              setEditForm((prev) => ({ ...prev, receivedAmount: e.target.value }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">{t("Paying Amount")}</label>
                          <input
                            type="number"
                            className="form-control"
                            value={editForm.payingAmount}
                            onChange={(e) =>
                              setEditForm((prev) => ({ ...prev, payingAmount: e.target.value }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">{t("Payment Type")}</label>
                          <Select
                            classNamePrefix="react-select"
                            options={paymentTypeOptions}
                            value={paymentTypeOptions.find((x) => x.value === editForm.paymentType) || null}
                            onChange={(selected) =>
                              setEditForm((prev) => ({ ...prev, paymentType: selected?.value || "cash" }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">{t("Payment Note")}</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={editForm.paymentNote}
                            onChange={(e) =>
                              setEditForm((prev) => ({ ...prev, paymentNote: e.target.value }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">{t("Sale Note")}</label>
                          <DefaultEditor
                            value={editForm.saleNote}
                            onChange={(e) =>
                              setEditForm((prev) => ({ ...prev, saleNote: e.target.value }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">{t("Staff Note")}</label>
                          <DefaultEditor
                            value={editForm.staffNote}
                            onChange={(e) =>
                              setEditForm((prev) => ({ ...prev, staffNote: e.target.value }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary add-cancel me-3" data-bs-dismiss="modal">
                      {t("Cancel")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary add-sale"
                      data-bs-dismiss="modal"
                      onClick={handleUpdateSale}
                    >
                      {t("Save Changes")}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="showpayment" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered stock-adjust-modal">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>{t("Show Payments")}</h4>
              </div>
              <button type="button" className="close" data-bs-dismiss="modal" aria-label={t("Close")}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="table-responsive rounded">
                <table className="table datatable">
                  <thead className="thead-light">
                    <tr>
                      <th>{t("Date")}</th>
                      <th>{t("Reference")}</th>
                      <th>{t("Amount")}</th>
                      <th>{t("Paid By")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingPayments ? (
                      <tr>
                        <td colSpan="4" className="text-center">{t("Loading...")}</td>
                      </tr>
                    ) : paymentsData.length > 0 ? (
                      paymentsData.map((payment) => (
                        <tr key={payment._id}>
                          <td>{payment?.createdAt ? new Date(payment.createdAt).toLocaleDateString() : "--"}</td>
                          <td>{payment?.orderId || "--"}</td>
                          <td>${Number(payment?.payingAmount || 0).toFixed(2)}</td>
                          <td>{payment?.type ? t(String(payment.type).toLowerCase() === "cash" ? "Cash" : String(payment.type).toLowerCase() === "card" ? "Card" : payment.type) : "--"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">{t("No payments found")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-3">
                <button type="button" className="btn btn-primary" onClick={fetchPayments}>
                  {t("Refresh Payments")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="createpayment" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>{t("Create Payments")}</h4>
              </div>
              <button type="button" className="close" data-bs-dismiss="modal" aria-label={t("Close")}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="modal-body">
              <div className="row">
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label className="form-label">{t("Date")}</label>
                    <div className="input-groupicon calender-input">
                      <DatePicker
                        className="form-control datetimepicker"
                        value={paymentForm.date ? dayjs(paymentForm.date) : null}
                        onChange={(date) => handlePaymentInput("date", date?.toDate?.() || new Date())}
                      />
                      <Calendar className="info-img" />
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 col-sm-12 col-12">
                  <div className="mb-3">
                    <label className="form-label">{t("Reference")}</label>
                    <input type="text" className="form-control" value={paymentForm.reference} readOnly />
                  </div>
                </div>

                <div className="col-lg-4 col-sm-12 col-12">
                  <div className="mb-3">
                    <label className="form-label">{t("Received Amount")}</label>
                    <div className="input-groupicon doller-input">
                      <DollarSign className="info-img" />
                      <input
                        type="number"
                        className="form-control ps-4"
                        value={paymentForm.receivedAmount}
                        onChange={(e) => handlePaymentInput("receivedAmount", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-12 col-12">
                  <div className="mb-3">
                    <label className="form-label">{t("Paying Amount")}</label>
                    <div className="input-groupicon doller-input">
                      <DollarSign className="info-img" />
                      <input
                        type="number"
                        className="form-control ps-4"
                        value={paymentForm.payingAmount}
                        onChange={(e) => handlePaymentInput("payingAmount", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-12 col-12">
                  <div className="mb-3">
                    <label className="form-label">{t("Payment Type")}</label>
                    <Select
                      classNamePrefix="react-select"
                      options={paymentTypeOptions}
                      value={paymentTypeOptions.find((x) => x.value === paymentForm.type) || null}
                      onChange={(selected) => handlePaymentInput("type", selected?.value || "cash")}
                    />
                  </div>
                </div>

                <div className="col-lg-4 col-sm-12 col-12">
                  <div className="mb-3">
                    <label className="form-label">{t("Change")}</label>
                    <input type="number" className="form-control" value={paymentForm.change} readOnly />
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="mb-3">
                    <label className="form-label">{t("description")}</label>
                    <DefaultEditor
                      value={paymentForm.paymentNote}
                      onChange={(e) => handlePaymentInput("paymentNote", e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="alert alert-info">
                    {t("Grand Total")}: ${grandTotal.toFixed(2)} | {t("Paid")}: ${paidAmount.toFixed(2)} | {t("Due")}: ${dueAmount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary me-2" data-bs-dismiss="modal">
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
                onClick={handleCreatePayment}
              >
                {t("Submit")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="delete-pos-sale-modal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content p-4">
            <div className="modal-body text-center">
              <h4>{t("Delete Sale")}</h4>
              <p>{t("Are you sure you want to delete this POS sale?")}</p>
              <div className="d-flex justify-content-center gap-2">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                  onClick={onDeleteClick}
                >
                  {t("Delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PosSalesModel.propTypes = {
  selectedOrder: PropTypes.object,
  salesDetails: PropTypes.object,
  onRefresh: PropTypes.func,
  onDeleteClick: PropTypes.func,
};

PosSalesModel.defaultProps = {
  selectedOrder: null,
  salesDetails: null,
  onRefresh: () => {},
  onDeleteClick: () => {},
};

export default PosSalesModel;
